import { useState } from "react";
import { Link } from "react-router";
import { Loader2, Receipt, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatApiError } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { formatDateBR } from "@/utils/dateUtils";
import { formatCentsBRL } from "@/utils/money";
import { useOmieCompetencias, useOsHistorico, useSincronizarOs } from "@/hooks/use-finance-omie";
import { coresDaSituacaoOs } from "@/components/finance/situacao-os";
import type { OmieEmpresaChave, OsHistorico, SituacaoOs } from "@/types/finance";

const TODAS = "todas";
const ITENS_POR_PAGINA = 50;

const EMPRESAS: Array<{ chave: OmieEmpresaChave; nome: string; cor: string }> = [
  { chave: "corisco", nome: "Corisco", cor: "border-sky-200 bg-sky-50 text-sky-700" },
  { chave: "faisca", nome: "Faísca", cor: "border-violet-200 bg-violet-50 text-violet-700" },
  { chave: "gota_serena", nome: "Gota Serena", cor: "border-teal-200 bg-teal-50 text-teal-700" },
];

const SITUACOES: Array<{ valor: SituacaoOs; rotulo: string }> = [
  { valor: "a_faturar", rotulo: "A faturar" },
  { valor: "faturada", rotulo: "Faturada" },
  { valor: "cancelada", rotulo: "Cancelada" },
];

function situacaoDaOs(os: OsHistorico): { rotulo: string; selo: string; fundo: string } {
  if (os.situacao) return { rotulo: os.situacao.rotulo, ...coresDaSituacaoOs(os.situacao.chave) };
  if (os.cancelada) return { rotulo: "Cancelada", ...coresDaSituacaoOs("cancelada") };
  if (os.faturada) return { rotulo: "Faturada", ...coresDaSituacaoOs("faturada") };
  return { rotulo: os.etapa ?? "A faturar", ...coresDaSituacaoOs("aguardando_revisao") };
}

function periodo(os: OsHistorico): string {
  if (!os.periodo_inicio) return "-";
  return os.periodo_fim
    ? `${formatDateBR(os.periodo_inicio)} a ${formatDateBR(os.periodo_fim)}`
    : `desde ${formatDateBR(os.periodo_inicio)}`;
}

export function OrdensServicoTab() {
  const [empresa, setEmpresa] = useState<string>(TODAS);
  const [situacao, setSituacao] = useState<string>(TODAS);
  const [competencia, setCompetencia] = useState<string>(TODAS);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const competenciasQuery = useOmieCompetencias();
  const sincronizar = useSincronizarOs();
  const { toast } = useToast();

  const sincronizarComOmie = async () => {
    try {
      const resultado = await sincronizar.mutateAsync();
      const novas = resultado.empresas.reduce((soma, empresa) => soma + empresa.novas, 0);
      const atualizadas = resultado.empresas.reduce((soma, empresa) => soma + empresa.atualizadas, 0);
      const semAcesso = resultado.sem_credenciais.length
        ? ` Sem acesso ao Omie: ${resultado.sem_credenciais.join(", ")}.`
        : "";
      toast({
        title: "Sincronização concluída",
        description: `${novas} OS novas, ${atualizadas} atualizadas.${semAcesso}`,
      });
    } catch (erro) {
      const formatado = formatApiError(erro);
      toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
    }
  };

  const query = useOsHistorico({
    empresa: empresa === TODAS ? undefined : (empresa as OmieEmpresaChave),
    situacao: situacao === TODAS ? undefined : (situacao as SituacaoOs),
    competencia: competencia === TODAS ? undefined : competencia,
    q: busca.trim() || undefined,
    page: pagina,
    page_size: ITENS_POR_PAGINA,
  });
  const dados = query.data;
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / ITENS_POR_PAGINA)) : 1;
  const competencias = [...new Set((competenciasQuery.data?.items ?? []).map((item) => item.competencia))];

  const filtrar = (aplicar: () => void) => {
    aplicar();
    setPagina(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Ordens de serviço das três empresas no Omie, ligadas às agências, profissionais e projetos daqui quando o
          casamento é certo. O código da proposta vem da observação da OS.
        </p>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={sincronizarComOmie} disabled={sincronizar.isPending}>
          {sincronizar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Sincronizar com o Omie
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={busca}
          onChange={(evento) => filtrar(() => setBusca(evento.target.value))}
          placeholder="Buscar cliente, profissional, código ou nº da OS"
          className="h-9 w-full max-w-sm"
        />
        <Select value={empresa} onValueChange={(valor) => filtrar(() => setEmpresa(valor))}>
          <SelectTrigger className="h-9 w-[150px]" aria-label="Empresa">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as empresas</SelectItem>
            {EMPRESAS.map((item) => (
              <SelectItem key={item.chave} value={item.chave}>
                {item.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={situacao} onValueChange={(valor) => filtrar(() => setSituacao(valor))}>
          <SelectTrigger className="h-9 w-[150px]" aria-label="Situação">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as situações</SelectItem>
            {SITUACOES.map((item) => (
              <SelectItem key={item.valor} value={item.valor}>
                {item.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={competencia} onValueChange={(valor) => filtrar(() => setCompetencia(valor))}>
          <SelectTrigger className="h-9 w-[170px]" aria-label="Competência">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as competências</SelectItem>
            {competencias.map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {dados && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Resumo rotulo="Total (sem canceladas)" valor={dados.totais.total_cents} detalhe={`${dados.total} OS`} />
          <Resumo rotulo="Faturado" valor={dados.totais.faturado_cents} />
          <Resumo rotulo="A faturar" valor={dados.totais.a_faturar_cents} destaque />
          <Resumo rotulo="Cancelado" valor={dados.totais.cancelado_cents} apagado />
        </div>
      )}

      {query.isLoading ? (
        <LoadingState variant="list" count={6} />
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : !dados || dados.items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhuma OS encontrada"
          description="O histórico do Omie aparece aqui depois da importação. Ajuste os filtros se já houver OS."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>OS</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Profissional</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Previsão</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Proposta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dados.items.map((os) => {
                  const empresaInfo = EMPRESAS.find((item) => item.chave === os.empresa);
                  const situacaoInfo = situacaoDaOs(os);
                  return (
                    <TableRow key={os.id} className={cn(situacaoInfo.fundo, os.cancelada && "opacity-60")}>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant="outline" className={cn("mr-1.5", empresaInfo?.cor)}>
                          {empresaInfo?.nome ?? os.empresa}
                        </Badge>
                        <span className="text-xs tabular-nums text-muted-foreground">nº {os.numero_os ?? "-"}</span>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        {os.agencia ? (
                          <Link to={`/agencias/${os.agencia.id}`} className="block truncate text-sm font-medium hover:underline">
                            {os.agencia.nome}
                          </Link>
                        ) : null}
                        <span className="block truncate text-xs text-muted-foreground" title={os.cliente_nome ?? ""}>
                          {os.cliente_nome ?? "-"}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm">
                        {os.freelancer ? (
                          <Link to={`/freelancers/${os.freelancer.id}`} className="block truncate hover:underline">
                            {os.freelancer.nome}
                          </Link>
                        ) : (
                          <span className="block truncate text-muted-foreground">{os.profissional_nome ?? "-"}</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{periodo(os)}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {os.data_previsao ? formatDateBR(os.data_previsao) : "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {os.data_vencimento ? formatDateBR(os.data_vencimento) : "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                        {formatCentsBRL(os.valor_cents)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("whitespace-nowrap text-[11px]", situacaoInfo.selo)}>
                          {situacaoInfo.rotulo}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {os.codigo_proposta ?? "-"}
                        {os.competencia && <span className="block">{os.competencia}</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <CardContent className="border-t py-3">
            <ListPagination currentPage={pagina} totalPages={totalPaginas} onPageChange={setPagina} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Resumo({
  rotulo,
  valor,
  detalhe,
  destaque,
  apagado,
}: {
  rotulo: string;
  valor: number;
  detalhe?: string;
  destaque?: boolean;
  apagado?: boolean;
}) {
  return (
    <Card>
      <CardContent className="space-y-0.5 p-4">
        <p className="text-xs text-muted-foreground">{rotulo}</p>
        <p
          className={cn(
            "text-xl font-semibold tabular-nums",
            destaque && "text-amber-700",
            apagado && "text-muted-foreground",
          )}
        >
          {formatCentsBRL(valor)}
        </p>
        {detalhe && <p className="text-[11px] text-muted-foreground">{detalhe}</p>}
      </CardContent>
    </Card>
  );
}
